import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, GraduationCap, Plane, FileWarning, AlertCircle, Bell, KanbanSquare } from "lucide-react";

interface TrainingStatusRow {
  module_id: string;
  title: string;
  status: "completed" | "expired" | "overdue" | "pending_due_soon" | "pending";
  days_until_due: number | null;
}
interface PtoRow { id: string; start_date: string; end_date: string; reason: string; request_status: string; }
interface NotifRow { id: string; preview: string; category: string | null; link: string | null; read_at: string | null; }
interface TaskRow {
  task_id: string;
  title: string;
  due_date: string | null;
  priority: "low" | "med" | "high" | null;
  board_id: string;
  board_name: string | null;
}

interface Item {
  id: string;
  icon: React.ReactNode;
  title: string;
  body?: string | null;
  to: string;
  tone: "bad" | "warn" | "ok";
  onSick?: () => void;
}

export function NeedsAttention({ onCallOutSick }: { onCallOutSick: () => void }) {
  const { employee } = useAuth();
  const [training, setTraining] = useState<TrainingStatusRow[]>([]);
  const [pto, setPto] = useState<PtoRow[]>([]);
  const [notifs, setNotifs] = useState<NotifRow[]>([]);
  const [tasks, setTasks] = useState<TaskRow[]>([]);

  useEffect(() => { if (employee?.id) void load(); }, [employee?.id]);
  async function load() {
    if (!employee?.id) return;
    const today = new Date().toISOString().slice(0, 10);
    const soonDate = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);
    const [{ data: t }, { data: p }, { data: n }, { data: asg }] = await Promise.all([
      supabase.from("hq_training_status")
        .select("module_id,title,status,days_until_due")
        .eq("employee_id", employee.id)
        .in("status", ["overdue", "expired", "pending_due_soon"]),
      supabase.from("hq_pto_requests")
        .select("id,start_date,end_date,reason,request_status")
        .eq("employee_id", employee.id)
        .in("request_status", ["denied"])
        .gte("end_date", today)
        .limit(3),
      supabase.from("hq_notifications")
        .select("id,preview,category,link,read_at")
        .eq("recipient_id", employee.id)
        .is("read_at", null)
        .in("category", ["pto_denied", "review_sent", "discipline", "callout_sick"])
        .order("created_at", { ascending: false })
        .limit(5),
      // Tasks assigned to me, not completed, due today/overdue/within 2 days
      supabase
        .from("hq_task_assignees")
        .select("task_id, hq_tasks!inner(id,title,due_date,priority,completed_at,board_id,hq_task_boards(name))")
        .eq("employee_id", employee.id)
        .is("hq_tasks.completed_at", null)
        .not("hq_tasks.due_date", "is", null)
        .lte("hq_tasks.due_date", soonDate),
    ]);
    setTraining((t as TrainingStatusRow[]) ?? []);
    setPto((p as PtoRow[]) ?? []);
    setNotifs((n as NotifRow[]) ?? []);

    interface RawAssignee {
      task_id: string;
      hq_tasks?: {
        id: string;
        title: string;
        due_date: string | null;
        priority: "low" | "med" | "high" | null;
        board_id: string;
        hq_task_boards?: { name: string | null } | { name: string | null }[] | null;
      } | Array<{
        id: string; title: string; due_date: string | null;
        priority: "low" | "med" | "high" | null; board_id: string;
        hq_task_boards?: { name: string | null } | { name: string | null }[] | null;
      }> | null;
    }
    const rawRows = (asg as unknown as RawAssignee[]) ?? [];
    const taskRows: TaskRow[] = rawRows
      .map(r => {
        const task = Array.isArray(r.hq_tasks) ? r.hq_tasks[0] : r.hq_tasks;
        if (!task) return null;
        const boardObj = Array.isArray(task.hq_task_boards) ? task.hq_task_boards[0] : task.hq_task_boards;
        return {
          task_id: r.task_id,
          title: task.title,
          due_date: task.due_date,
          priority: task.priority,
          board_id: task.board_id,
          board_name: boardObj?.name ?? null,
        };
      })
      .filter((x): x is TaskRow => x !== null)
      .sort((a, b) => {
        const ad = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        const bd = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        return ad - bd;
      });
    setTasks(taskRows);
  }

  const items = useMemo<Item[]>(() => {
    const list: Item[] = [];
    const today = new Date().toISOString().slice(0, 10);

    // Tasks assigned to me (overdue + today + soon) — highest signal "do this now"
    tasks.forEach(t => {
      const isOverdue = !!t.due_date && t.due_date < today;
      const isToday = t.due_date === today;
      const isHigh = t.priority === "high";
      const body = isOverdue
        ? `Overdue · ${t.board_name ?? "Board"}`
        : isToday ? `Due today · ${t.board_name ?? "Board"}`
        : `Due ${t.due_date} · ${t.board_name ?? "Board"}`;
      list.push({
        id: "task-" + t.task_id,
        icon: <KanbanSquare size={16} />,
        title: t.title,
        body,
        to: `/tasks/${t.board_id}`,
        tone: isOverdue || isHigh ? "bad" : isToday ? "warn" : "ok",
      });
    });

    // Training items
    training.forEach(t => {
      const isUrgent = t.status === "overdue" || t.status === "expired";
      list.push({
        id: "train-" + t.module_id,
        icon: <GraduationCap size={16} />,
        title: t.title,
        body: t.status === "overdue" ? "Training overdue"
          : t.status === "expired" ? "Needs recertification"
          : t.days_until_due != null ? `Due in ${t.days_until_due} days` : "Due soon",
        to: "/training",
        tone: isUrgent ? "bad" : "warn",
      });
    });

    // Denied PTO that hasn't been re-handled
    pto.forEach(p => {
      list.push({
        id: "pto-" + p.id,
        icon: <Plane size={16} />,
        title: `Time off denied: ${p.start_date}`,
        body: p.reason,
        to: "/pto",
        tone: "warn",
      });
    });

    // High-signal unread notifications
    notifs.forEach(n => {
      list.push({
        id: "notif-" + n.id,
        icon: <Bell size={16} />,
        title: n.preview.length > 60 ? n.preview.slice(0, 60) + "…" : n.preview,
        body: null,
        to: n.link || "/",
        tone: "warn",
      });
    });

    return list;
  }, [training, pto, notifs, tasks]);

  const hasBad = items.some(i => i.tone === "bad");

  if (employee?.role === "kiosk") return null;

  // Always show the sick day quick link
  const sickItem: Item = {
    id: "sick-day",
    icon: <AlertCircle size={16} />,
    title: "Not feeling well? Call out sick today",
    body: "One tap submits today's time off and notifies admin",
    to: "#",
    tone: "warn",
    onSick: onCallOutSick,
  };

  return (
    <div className={`card ${hasBad ? "border-l-4 border-bad bg-bad-bg/30" : items.length > 0 ? "border-l-4 border-warn bg-warn-bg/30" : ""}`}>
      <div className="mb-2 flex items-center gap-2">
        {hasBad ? <AlertTriangle size={18} className="text-bad" /> : <FileWarning size={18} className="text-warn" />}
        <h2 className="text-base font-semibold">
          {items.length === 0 ? "Your day at a glance" : "Needs your attention"}
        </h2>
        {items.length > 0 && (
          <span className="ml-auto rounded-full bg-bad px-2 py-0.5 text-xs font-bold text-white">{items.length}</span>
        )}
      </div>

      <div className="space-y-1.5">
        {items.map(it => (
          <Link
            key={it.id}
            to={it.to}
            className="flex items-start gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-line transition hover:ring-teal-dark"
          >
            <span className={`mt-0.5 ${it.tone === "bad" ? "text-bad" : "text-warn"}`}>{it.icon}</span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{it.title}</div>
              {it.body && <div className="truncate text-xs text-gray2">{it.body}</div>}
            </div>
          </Link>
        ))}

        <button
          onClick={onCallOutSick}
          className="flex w-full items-start gap-3 rounded-lg bg-white px-3 py-2 ring-1 ring-line transition hover:ring-orange-400 text-left"
        >
          <span className="mt-0.5 text-orange-600">{sickItem.icon}</span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">{sickItem.title}</div>
            <div className="truncate text-xs text-gray2">{sickItem.body}</div>
          </div>
        </button>
      </div>
    </div>
  );
}
